/**
 * Lightweight client-side selfie quality heuristics. These are UX guards only —
 * they never produce a liveness score and never replace server-side checks.
 * Goals: catch obviously bad frames (too dark / too bright / near-blank) so the
 * applicant can retake before upload.
 */

export type QualityIssue = 'tooDark' | 'tooBright' | 'lowDetail';

export interface QualityResult {
  ok: boolean;
  issues: QualityIssue[];
}

/** Thresholds tuned for 0–255 luma (Rec. 601). */
const DARK = 38;
const BRIGHT = 230;
const MIN_VARIANCE = 80;

/**
 * Grade a sample of RGB pixels (flat RGBA buffer, 4 bytes per pixel).
 * Pure — unit-tested without a canvas implementation.
 */
export function assessPixels(pixels: ArrayLike<number>, sampleStride = 4): QualityResult {
  if (pixels.length < 4) return { ok: true, issues: [] };

  const step = Math.max(4, sampleStride * 4); // sample every Nth pixel
  let count = 0;
  let sum = 0;
  let sumSq = 0;
  for (let i = 0; i + 2 < pixels.length; i += step) {
    const r = pixels[i] ?? 0;
    const g = pixels[i + 1] ?? 0;
    const b = pixels[i + 2] ?? 0;
    const y = 0.299 * r + 0.587 * g + 0.114 * b;
    sum += y;
    sumSq += y * y;
    count += 1;
  }
  if (count === 0) return { ok: true, issues: [] };

  const mean = sum / count;
  const variance = sumSq / count - mean * mean;
  const issues: QualityIssue[] = [];
  if (mean < DARK) issues.push('tooDark');
  if (mean > BRIGHT) issues.push('tooBright');
  if (variance < MIN_VARIANCE) issues.push('lowDetail');

  return { ok: issues.length === 0, issues };
}

/**
 * Sample a canvas center region (face hole) and grade it.
 * Fail-open if 2d context / getImageData is unavailable (jsdom, tainted).
 */
export function assessSelfieQuality(canvas: HTMLCanvasElement): QualityResult {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx || canvas.width < 2 || canvas.height < 2) {
    return { ok: true, issues: [] };
  }

  const sx = Math.floor(canvas.width * 0.25);
  const sy = Math.floor(canvas.height * 0.2);
  const sw = Math.floor(canvas.width * 0.5);
  const sh = Math.floor(canvas.height * 0.55);
  let data: ImageData;
  try {
    data = ctx.getImageData(sx, sy, Math.max(1, sw), Math.max(1, sh));
  } catch {
    return { ok: true, issues: [] };
  }

  return assessPixels(data.data, 4);
}
